import React from "react";
import * as styles from "@styles/Subscription.css";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardTitle } from "@components/ui/card";
import { useIoState } from "@utils/useIoState";

import { makeSubscriptionPlan } from "@ps-bridge/models/brands/subscriptionPlan";
import { unwrap as unwrapJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import * as Option from "@utils/Option";
import PlanSelectorView from "@view/payment/choose-subscription/PlanSelector.view";
import PromoCodeSectionView from "@view/payment/choose-subscription/PromoCodeSection.view";
import PromoCodeErrorPanel from "@view/panels/payment/choose-subscription/PromoCodeError.panel";
import SelectedPackagePanel from "@view/panels/payment/choose-subscription/SelectedPackage.panel";
import type { ChooseSubscriptionViewProps } from "@ps-bridge/contracts/chooseSubscription";
import * as IO from "@ps-bridge/interop/io";
import { visibilityPhaseNamespace as visibilityPhase } from "@ps-bridge/models/ui/visibilityPhase";
import type { VisibilityPhase } from "@ps-bridge/models/ui/visibilityPhase";

type PlanOption = {
  value: "PayAsYouGo" | "Standard" | "Enterprise";
  label: string;
  price: string;
  detail: string;
  highlight: string;
};

type SubscriptionHelperTextKey =
  | "PaymentFailed"
  | "PaymentNotYetConfigured"
  | "FreeTrialMode"
  | "Paid"
  | "Canceled";

const planOptions: PlanOption[] = [
  {
    value: "PayAsYouGo",
    label: "Pay-as-you-go",
    price: "$1.50 / page",
    detail: "No commitment. Audit trail included on every export.",
    highlight: "Flexible for early teams",
  },
  {
    value: "Standard",
    label: "Standard",
    price: "$50 / month",
    detail: "$1.00 per processed page for recurring packets.",
    highlight: "Best for steady workflows",
  },
  {
    value: "Enterprise",
    label: "Enterprise",
    price: "$200 / month",
    detail: "$0.75 per processed page plus priority audit-trail support.",
    highlight: "Scale with dedicated support",
  },
];

const ChooseSubscriptionComponent: React.FC<ChooseSubscriptionViewProps> = ({
  onPackageSelection,
  onApplyPromoCode,
  subscriptionStatus,
  loading,
  loadingPromo,
  promoCodeErrorPhase,
  promoCodeErrorMessage,
}) => {
  const { read: selectedPackage, set: setSelectedPackage } = useIoState<string>(planOptions[0].value);
  const { read: promoCode, set: setPromoCode } = useIoState<string>("");
  const selectedPlan = planOptions.find((plan) => plan.value === selectedPackage) ?? planOptions[0];
  const selectedPackageLabel = selectedPlan.label;
  const isPayAsYouGo = selectedPlan.value === "PayAsYouGo";

  const subscriptionHelperText: Record<SubscriptionHelperTextKey, string> = {
    PaymentFailed: "There was an issue processing your previous payment, please update your information.",
    PaymentNotYetConfigured: isPayAsYouGo
      ? "Pay-as-you-go is billed only when pages are processed. There is no recurring subscription or trial-period billing step."
      : "Standard and Enterprise include a 30-day free trial, and there is no charge for canceling before the trial expires.",
    FreeTrialMode: isPayAsYouGo
      ? "You are currently in free trial mode. Pay-as-you-go switches you to direct usage billing once activated."
      : "You are currently in free trial mode. Upgrade to a paid plan before the trial expires.",
    Paid: "Thank you for being a valued subscriber! You can update your plan here.",
    Canceled: "Your previous subscription was canceled, but you can restart it by purchasing a plan below.",
  };

  const helperText =
    subscriptionHelperText[
      unwrapJwtSubscriptionStatus(subscriptionStatus) as SubscriptionHelperTextKey
    ] || "";
  const selectedPackagePhase: VisibilityPhase = selectedPackage
    ? visibilityPhase.visible
    : visibilityPhase.hidden;
  const billingFootnote = isPayAsYouGo
    ? "By proceeding, you authorize direct usage billing on the payment method you add. Pay-as-you-go does not create a recurring subscription or wait for a trial period to end."
    : "By proceeding, you authorize billing to begin at the end of the trial period. You may cancel anytime through your account settings to avoid future charges.";
  const promoCodeErrorView = (
    <PromoCodeErrorPanel
      phase={promoCodeErrorPhase}
      message={promoCodeErrorMessage}
    />
  );

  return (
    <div
      className={styles.container}
      data-testid="page-chooseSubscription"
      data-page-state="ready"
    >
      <Card className={styles.card}>
        <CardContent>
          <CardTitle className={styles.title}>Select a Subscription Package</CardTitle>
          <p>{helperText}</p>

          <PlanSelectorView
            plans={planOptions}
            selectedPackage={selectedPackage}
            onSelectPlan={setSelectedPackage}
          />

          <PromoCodeSectionView
            promoCode={promoCode}
            loading={loading}
            loadingPromo={loadingPromo}
            errorView={promoCodeErrorView}
            onPromoCodeChange={setPromoCode}
            onApplyPromoCode={onApplyPromoCode}
          />

          <Button
            data-testid="choose-subscription-continue"
            onClick={() => {
              IO.run(onPackageSelection(makeSubscriptionPlan(selectedPackage)));
            }}
            disabled={!selectedPackage || loading}
            className={styles.button}
            asChild={Option.none()}
          >
            {loading ? "Processing..." : "Continue to Payment"}
          </Button>

          <SelectedPackagePanel
            phase={selectedPackagePhase}
            selectedPackage={selectedPackageLabel}
            isPayAsYouGo={isPayAsYouGo}
          />
          <p className={styles.selectedPackageText}>{billingFootnote}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChooseSubscriptionComponent;
